import { createLog } from '@iv/shared/src/log';
import { ChannelMessage, ChannelNameSpace, IParentBridge, createParentBridge } from '../MessageChannel';
import { createPlayer } from '@iv/adapter-player';
import { createBridge } from '@iv/adapter-bridge';
import PlayerBridge, { IPlayerInfo } from '@iv/adapter-player/src/interface';
import AdapterBridge from '@iv/adapter-bridge/src/interface';
import events from 'events';
import { PlayerType, BridgeType } from '@iv/shared/src/enum';
import { SLA, Indicators, SlaInitType } from '@iv/shared/src/reportSla';

import Interactor from './interactor';
import DataProcessor from './dataProcessor';

import { classTracker, trackerEnv } from '@iv/shared/src/tracker';

import { isSupportWorkerAndProxy, initWebSocket } from '../Worker/common';
import InteractWorker from '../Worker/index.worker';
import axios from 'axios';

const log = createLog('InteractVideo');

export enum ComponentEnvType {
  IFRAME = 1,
  WORKER = 2,
  SERVER = 3,
}

interface InitParam {
  readonly containerId?: string;
  readonly businessId: string;
  playerInstance?: object;
  config?: ICfg;
  playerType: PlayerType;
  bridgeType: BridgeType;
  useLocalMode: boolean;
  iframeSrc?: string;
  fetchLib?: Function;
  apiWhiteList?: IApiPermission;
  apiBlackList?: IApiPermission;
  environment?: ComponentEnvType;
}

/**
 * @description InteractVideo
 * @example
 * ```javascript
 * // 使用方式
 * const interactVideo = new iv.InteractVideo({
 *   playerType: 'tenvideoH5',
 *   businessId: 'xxxx'
 * });
 *
 * // 事件
 * interactVideo.on('readyToPlay', function() {
 *   console.log('interactVideo readyToPlay.');
 * });
 * ```
 */
@classTracker(
  false, trackerEnv.parent,
  [
    'createChannel',
    'createDataProcessor',
    'createInteractPlayer',
    'createInteractBridge',
    'beforeCreateParentBridge',
  ],
)
class InteractVideo {
  /**
   * @description 互动视频准备完成事件
   * @example
   * ```javascript
   * interactVideo.on('readyToPlay', () => {});
   * ```
   * @event
   */
  public static readyToPlay: string;

  /**
   * @description 互动视频错误事件
   * @param 错误信息
   * @example
   * ```javascript
   * interactVideo.on('error', (err: Error) => {});
   * ```
   * @event
   */
  public static error: string;

  /**
   * @description 互动视频开始播放事件
   * @param videoInfo {@link IVideoInfo}
   * @example
   * ```javascript
   * interactVideo.on('beginPlay', (videoInfo: IVideoInfo) => {});
   * ```
   * @event
   */
  public static beginPlay: string;

  /**
   * @description 互动视频互动层显示事件
   * @example
   * ```javascript
   * interactVideo.on('showInteract', () => {});
   * ```
   * @event
   */
  public static showInteract: string;

  /**
   * @description 互动视频互动层隐藏事件
   * @example
   * ```javascript
   * interactVideo.on('hideInteract', () => {});
   * ```
   * @event
   */
  public static hideInteract: string;

  /**
   * @description 互动视频bridge实例初始化完成事件
   * @example
   * ```javascript
   * interactVideo.on('bridgeRady', (bridge) => {});
   * ```
   * @event
   */
  public static bridgeRady: string;

  /**
   * @description 互动视频engine实例初始化完成事件
   * @example
   * ```javascript
   * interactVideo.on('engineReady', (engine) => {});
   * ```
   * @event
   */
  public static engineReady: string;

  /**
   * @description 互动视频player实例初始化完成事件
   * @example
   * ```javascript
   * interactVideo.on('playerReady', (player) => {});
   * ```
   * @event
   */
  public static playerReady: string;

  /**
   * @description 隐藏直出页左上角返回按钮
   * @example
   * ```javascript
   * interactVideo.on('hideBackBtn', () => {});
   * ```
   * @event
   */
  public static hideBackBtn: string;

  /**
   * @description 展示直出页左上角返回按钮
   * @example
   * ```javascript
   * interactVideo.on('showBackBtn', () => {});
   * ```
   * @event
   */
  public static showBackBtn: string;

  private player: PlayerBridge;
  private bridge: AdapterBridge;
  private channel: IParentBridge;
  private engine: Interactor;
  private msg: events.EventEmitter = new events.EventEmitter();
  private nsUnReadyBuffer: { [key: string]: Function[] } = {};
  private apiWhiteList: IApiPermission;
  private apiBlackList: IApiPermission;

  /**
   *
   * @constructor
   * @param {object} param
   *        containerId: 容器ID，没有默认为document.body
   *        businessId: 接入业务ID
   *        playerType: 播放器类型（播放适配层）
   *        bridgeType: 互动Bridge类型（互动适配层）
   *        playerInstance: 播放器实例，一般H5播放器下需要该参数
   *        config: 配置信息
   *        useLocalMode: 本地模式，若是，v数值、kv存储，都不请求后台
   *        iframeSrc: 容器地址，一般没有必要自定义
   *        fetchLib: 外部传入 fetchLib 方法，提供给组件使用网络请求
   *        apiWhiteList || apiBlackList: 组件可调用API的黑白名单
   *          例如：apiBlackList: { 'engine': ['action'] }
   *        environment: 组件运行环境，1:iframe，2:worker，3:server，默认是1
   */
  public constructor({
    containerId,
    businessId,
    playerInstance,
    config,
    playerType = PlayerType.tenvideoH5,
    bridgeType = BridgeType.tenvideoWeb,
    useLocalMode = false,
    iframeSrc,
    fetchLib = axios,
    apiWhiteList = {},
    apiBlackList = {},
    environment = ComponentEnvType.IFRAME,
  }: InitParam) {
    log(`create, init param: ${JSON.stringify({
      containerId,
      playerType,
      bridgeType,
      iframeSrc,
    })}`);
    if (PRODUCTION && !businessId) {
      console.warn('businessId is necessary!');
    }
    this.apiWhiteList = apiWhiteList;
    this.apiBlackList = apiBlackList;
    // SLA 上报
    const sla = new SLA(businessId);
    // 初始化的时候进行一次指标上报
    sla.reportSla({
      value: 1,
      id: Indicators.INIT,
    });

    // 创建容器消息通道
    const channelPromise = this.createChannel(containerId, iframeSrc, environment);
    // 获取配置
    const { dataProcessor, configPromise } = this.createDataProcessor(config);
    // 播放器适配层
    const playerPromise = this.createInteractPlayer(playerType, playerInstance);
    // 互动适配层
    const bridgePromise = this.createInteractBridge(bridgeType, containerId);

    Promise.all([
      channelPromise,
      configPromise,
      playerPromise,
      bridgePromise,
    ]).then((data) => {
      this.channel = data[0] as IParentBridge;
      const playerInfo = data[2] as IPlayerInfo;
      this.nsReady(ChannelNameSpace.player, playerInfo.player);
      const adapterBridge = data[3] as AdapterBridge;
      this.nsReady(ChannelNameSpace.bridge, adapterBridge);

      // 初始化互动生命周期
      const engine = new Interactor({
        playerInfo,
        dataProcessor,
        ivMsg: this.msg,
        parentBridge: this.channel,
        adapterBridge,
        useLocalMode,
        businessId,
        fetchLib,
      });
      this.nsReady(ChannelNameSpace.engine, engine);
      engine.init();
      sla.reportSla({ value: SlaInitType.SUCCESS, id: Indicators.TOTAL });
    })
      .catch((err) => {
        sla.reportSla({ value: SlaInitType.FAIL, id: Indicators.TOTAL });
        this.msg.emit('error', err);
      });
  }

  /**
   * @description 事件监听
   * @param eventName 事件名
   * @param callback 回调函数
   */
  public on(eventName: string, callback: (...args: any[]) => void) {
    this.msg.on(eventName, callback);
  }

  /**
   * @description 互动视频开始播放
   * @example
   * ```javascript
   * interactVideo.startPlay();
   * ```
   */
  public startPlay() {
    this.engine.ready();
  }

  /**
   * 销毁消息通道
   */
  public destroy() {
    this.bridge?.destroyAllSound();
    this.channel?.destroy();
  }

  /**
   * @description 外部设置互动视频跳转到其他节点
   * @param param
   * @example
   * ```javascript
   * interactVideo.action({jump: 'nodeId'});
   * ```
   */
  public action(param: IAction) {
    this.engine.action(param);
  }

  /**
   * 创建消息通道
   * @param
   */
  private async createChannel(containerId: string, iframeSrc: string, environment: ComponentEnvType) {
    const ivComps = window.iv.InteractComps;
    const supportWorker = await this.beforeCreateParentBridge(environment);
    // 这里之前挖了个坑，由于外部用户引用后重置了iv全局变量，这里需要先保存再重新赋值
    window.iv.InteractComps = ivComps;

    const bridge = await createParentBridge({
      containerId,
      iframeSrc,
      useWorker: supportWorker,
      init: (data: ChannelMessage) => {
        const { ns } = data;
        if (this[ns as ChannelNameSpace]) {
          return this.fixMessage(data);
        }
        if (!this.nsUnReadyBuffer[ns]) {
          this.nsUnReadyBuffer[ns] = [];
        }
        return new Promise((resolve) => {
          this.nsUnReadyBuffer[ns].push(() => {
            const res = this.fixMessage(data);
            if (res?.then) {
              res.then(resolve);
            } else {
              resolve(res);
            }
          });
        });
      },
    });
    return bridge;
  }

  /**
   * 执行对应命名空间下的方法，并且把执行结果返回
   * @param
   */
  private fixMessage({ nodeId, ns, method, data }: ChannelMessage) {
    const tns: any = this[ns as ChannelNameSpace];
    if (method === 'on') {
      tns.on(data.event, (param: any) => {
        this.channel.send({
          nodeId,
          ns,
          method: data.event,
          data: param,
        });
      });
    } else {
      if (!tns[method]) {
        log(`${ns}.${method} not implement`);
        return;
      }
      if (!this.checkApiPermission(ns as ChannelNameSpace, method)) {
        return {
          code: -11,
          message: 'no access to this method',
        };
      }
      if (this.engine) {
        const currentNode = this.engine.getCurrentNode();
        if (currentNode && nodeId && nodeId !== currentNode.id) {
          return 'comps has been destroyed';
        }
      }
      const res = tns[method](...data);
      return res;
    }
  }

  /**
   * 消息通道 ready 处理，把之前缓存的回调执行一次
   * @param ns
   * @param obj
   */
  private nsReady(ns: ChannelNameSpace, obj: any) {
    this[ns] = obj;

    const buffer = this.nsUnReadyBuffer[ns];
    if (buffer) {
      buffer.forEach((callback) => {
        callback();
      });
    }
    this.nsUnReadyBuffer[ns] = [];
    this.msg.emit(`${ns}Ready`, obj);
  }

  /**
   * 根据初始化的黑白名单，检查组件调用的API的权限
   * @param ns
   * @param method
   */
  private checkApiPermission(ns: ChannelNameSpace, method: string) {
    const isInList = (list: Array<string>) => list.some(currentValue => method === currentValue);
    const whiteList = this.apiWhiteList[ns];
    const blackList = this.apiBlackList[ns];

    if (whiteList && Array.isArray(whiteList)) {
      return isInList(whiteList);
    }
    if (blackList && Array.isArray(blackList)) {
      return !isInList(blackList);
    }
    return true;
  }

  /**
   * 创建父页面bridge前的环境初始化工作
   * @param environment
   */
  private async beforeCreateParentBridge(environment: ComponentEnvType) {
    // iframe
    if (!environment || environment === ComponentEnvType.IFRAME) {
      return false;
    }
    // 二次确认是否真的使用webworker
    const supportWorker = isSupportWorkerAndProxy();
    if (!supportWorker) {
      return false;
    }
    // worker
    if (environment === ComponentEnvType.WORKER) {
      window.interactWorker = new InteractWorker();
      return true;
    }
    // server
    const ws: any = await initWebSocket();
    window.interactWorker = {
      postMessage: (data: any) => {
        ws.send(`${JSON.stringify(data)}`);
      },
    };
    ws.onmessage = (e: MessageEvent) => {
      if (e.data === 'ping') {
        ws.send('pong');
        return;
      }
      const data = JSON.parse(e.data);
      window.interactWorker.onmessage({ data });
    };
    return true;
  }

  /**
   * 创建互动数据配置请求和处理器
   * @param config
   */
  private createDataProcessor(config: ICfg) {
    const dataProcessor = new DataProcessor();
    const configPromise = dataProcessor.getConfig(config);

    return {
      dataProcessor,
      configPromise,
    };
  }

  /**
   * 创建互动播放器适配层
   * @param playerType
   * @param playerInstance
   */
  private createInteractPlayer(playerType: PlayerType, playerInstance: object) {
    return createPlayer({
      type: playerType,
      playerInstance,
    });
  }

  /**
   * 创建互动适配层
   * @param bridgeType
   * @param containerId
   */
  private createInteractBridge(bridgeType: BridgeType, containerId: string) {
    return createBridge(bridgeType, {
      containerId,
    });
  }
}

export default InteractVideo;
